import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DocMeta, DocIndex, loadDoc, loadManifest } from "../../docs/loader";
import MarkdownPage from "./MarkdownPage";
import { FlavorBadge, PersonaBadge, PlatformBadge, TagBadge } from "./Badges";
import Breadcrumbs from "./Breadcrumbs";
import DocToc from "./DocToc";
import { Link } from "react-router-dom";

const DocPage: React.FC = () => {
  const params = useParams();
  const lang = (params.lang as string) || "en";
  const slug = (params["*"] as string) || "getting-started/overview";
  const [meta, setMeta] = useState<DocMeta | null>(null);
  const [content, setContent] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [manifest, setManifest] = useState<DocIndex[] | null>(null);

  useEffect(() => {
    setError(null);
    loadDoc(slug)
      .then(({ meta, content }) => {
        setMeta(meta);
        setContent(content);
      })
      .catch((e) => setError(String(e)));
  }, [slug]);

  useEffect(() => {
    loadManifest().then(setManifest).catch(console.error);
  }, []);

  const { prevDoc, nextDoc } = useMemo(() => {
    if (!manifest || !meta) return { prevDoc: null, nextDoc: null };
    // constrain to same section for linear reading
    const sectionDocs = manifest
      .filter((d) => d.section === meta.module)
      .sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title)
      );
    const idx = sectionDocs.findIndex((d) => d.slug === meta.slug);
    const prevDoc = idx > 0 ? sectionDocs[idx - 1] : null;
    const nextDoc =
      idx >= 0 && idx < sectionDocs.length - 1 ? sectionDocs[idx + 1] : null;
    return { prevDoc, nextDoc };
  }, [manifest, meta]);

  if (error) return <div className="text-red-600">{error}</div>;
  if (!meta) return <div>Loading…</div>;

  return (
    <div className="flex w-full gap-8">
      <div className="flex-1 min-w-0">
        <Breadcrumbs />
        <h1 className="text-2xl font-bold mb-2">{meta.title}</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mb-4">
          <span>Version {meta.version}</span>
          <span>•</span>
          <span>Updated {meta.lastUpdated}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {meta.platforms.map((p) => (
            <PlatformBadge key={p} platform={p} />
          ))}
          {meta.flavors.map((f) => (
            <FlavorBadge key={f} flavor={f} />
          ))}
          {meta.personas.map((p) => (
            <PersonaBadge key={p} persona={p} />
          ))}
          {meta.tags.map((t) => (
            <TagBadge key={t} label={t} />
          ))}
        </div>
        {meta.hero && (
          <img src={meta.hero} alt="hero" className="w-full rounded-lg mb-6" />
        )}
        <MarkdownPage content={content} meta={meta} />

        {/* Prev / Next navigation */}
        <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            {prevDoc && (
              <Link
                to={`/${lang}/docs/${prevDoc.slug}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
              >
                ← Previous:{" "}
                <span className="font-medium truncate max-w-[240px]">
                  {prevDoc.title}
                </span>
              </Link>
            )}
          </div>
          <div className="min-w-0 flex-1 text-right">
            {nextDoc && (
              <Link
                to={`/${lang}/docs/${nextDoc.slug}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
              >
                <span className="font-medium truncate max-w-[240px]">
                  {nextDoc.title}
                </span>{" "}
                :Next →
              </Link>
            )}
          </div>
        </div>
      </div>
      <DocToc />
    </div>
  );
};

export default DocPage;
